import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { EvidenceSlotCard } from '@/shared/components/evidence/evidence-slot-card'
import {
  checkSessionCopies,
  uploadSessionFile,
  type SessionUploadParams,
} from '@/shared/lib/evidence/session-api'
import type { Slot } from '@/shared/components/evidence/types'
import { evidenceSlotAnchorId } from '@/consumer/lib/evidence-slot-anchor'

export interface EvidenceChecklistData {
  token: string
  slots: Slot[]
}

interface ConsumerEvidenceChecklistProps {
  claimUuid: string
  checklist: EvidenceChecklistData
  /** Slots a submit found short; each shows how many files it still needs. */
  flaggedSlotIds?: ReadonlySet<string>
}

/**
 * WPS-138 #6: the categorised evidence checklist rendered inline on the consumer
 * claim view - the same `EvidenceSlotCard` (categories, example photos, helper
 * text, take/choose photo) the Upload Evidence page uses, but without delete.
 * Uploads go straight into the claim's active session via the shared,
 * same-origin session client; a successful upload refreshes the claim so the
 * server-side slot state reconciles with the card's optimistic view.
 */
export function ConsumerEvidenceChecklist({
  claimUuid,
  checklist,
  flaggedSlotIds,
}: ConsumerEvidenceChecklistProps) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const sorted = [...checklist.slots].sort((a, b) => a.ordering - b.ordering)

  const uploadFn = useCallback(
    async (params: SessionUploadParams) => {
      const uploaded = await uploadSessionFile(checklist.token, params)
      qc.invalidateQueries({ queryKey: ['consumer-claim', claimUuid] })
      return uploaded
    },
    [checklist.token, claimUuid, qc],
  )

  return (
    <div className="space-y-3">
      {sorted.map((slot, index) => {
        const missing = slot.min - slot.uploads.length
        const error =
          flaggedSlotIds?.has(slot.id) && missing > 0
            ? t('evidence.moreRequired', '{{count}} more required', { count: missing })
            : undefined
        return (
          <div key={slot.id} id={evidenceSlotAnchorId(slot.id)}>
            <EvidenceSlotCard
              slot={slot}
              index={index}
              uploadFn={uploadFn}
              error={error}
              checkCopies={(hashes) => checkSessionCopies(checklist.token, slot.id, hashes)}
            />
          </div>
        )
      })}
    </div>
  )
}
